/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { PureComponent } from 'react';
import {
  node,
  string,
  bool,
  number,
  oneOf,
  oneOfType,
  func,
  array,
  object,
} from 'prop-types';
import classNames from 'clsx';
import { NavLink } from 'react-router-dom';

import p from 'polyglot/polyglot';

import { createUrl } from 'utils/urlHelper';

import TextInput from './TextInput/TextInput';
import Select from './Select/Select';
import Checkbox from './Checkbox/Checkbox';
import TextArea from './TextArea/TextArea';
import Image from './Image/Image';
import CleaveInput from './CleaveInput/CleaveInput';
import TagsInput from './TagsInput/TagsInput';
import DatePickerInput from './DatePickerInput/DatePickerInput';
import GooglePlacesInput from './GooglePlacesInput/GooglePlacesInput';
import Message from '../Message/Message';
import ToolTip from 'components/ToolTip/ToolTip';
import BlackTieIcon from 'components/BlackTieIcon/BlackTieIcon';

import styles from './input.scss';

/**
 * This is the Input component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export default class Input extends PureComponent {
  /**
   * Declare expected prop types.
   *
   * @type {Object}
   */
  static propTypes = {
    children: node,
    label: string,
    description: string,
    type: oneOf([
      'textarea',
      'text',
      'select',
      'tel',
      'password',
      'image',
      'file',
      'checkbox',
      'cleave',
      'datepicker',
      'tags',
      'googleplaces',
      'hidden',
    ]).isRequired,
    name: string.isRequired,
    placeholder: string,
    value: oneOfType([string, number, bool]),
    options: oneOfType([array, object]),
    focus: bool.isRequired,
    preview: string,
    error: string,
    disabled: bool,
    readOnly: bool,
    autoComplete: string,
    required: bool.isRequired,
    multiple: bool,
    constraints: object,
    replaceField: string,
    mustMatch: string,
    format: string,
    validate: func.isRequired,
    setFocus: func.isRequired,
    colWidth: number,
    mobileColWidth: number,
    resendPhoneLink: bool,
    clearRight: bool,
    light: bool,
    index: number,
    tool_tip: string,
  };

  /**
   * Declare default props.
   *
   * @type {Object}
   */
  static defaultProps = {
    preview: '',
    constraints: '',
    replaceField: null,
    mustMatch: null,
    format: null,
    error: '',
    multiple: false,
    disabled: false,
    readOnly: false,
    autoComplete: '',
    resendPhoneLink: false,
    light: false,
    clearRight: false,
    tool_tip: '',
  };

  /**
   * Event handler that passes values to parent component.
   *
   * @param {Object} event
   */
  onChangeHandler = event => {
    const {
      type,
      name,
      label,
      required,
      constraints,
      mustMatch,
      replaceField,
      validate,
    } = this.props;
    const value =
      type === 'checkbox' ? event.target.checked : event.target.value;

    validate(
      name,
      label,
      value,
      required,
      constraints,
      mustMatch,
      replaceField
    );
  };

  /**
   * Event handler for when the input gains focus.
   */
  onFocusHandler = () => {
    const { name, setFocus } = this.props;

    setFocus(name);
  };

  /**
   * Event handler for when the input looses focus.
   */
  onBlurHandler = () => {
    const {
      name,
      label,
      value,
      required,
      constraints,
      mustMatch,
      replaceField,
      validate,
      setFocus,
    } = this.props;

    setFocus(null);

    validate(
      name,
      label,
      value,
      required,
      constraints,
      mustMatch,
      replaceField
    );
  };

  /**
   * Render input based on type.
   *
   * @return {Object}
   */
  renderInput() {
    const {
      type,
      name,
      focus,
      label,
      placeholder,
      value,
      preview,
      required,
      multiple,
      disabled,
      readOnly,
      autoComplete,
      options,
      error,
      index,
      format,
    } = this.props;

    let input = null;

    switch (type) {
      case 'checkbox':
      case 'radio':
        input = (
          <Checkbox
            {...{
              name,
              label,
              value: !!value,
              required,
              disabled,
              readOnly,
              error: !!error,
              onChange: this.onChangeHandler,
            }}
          />
        );
        break;

      case 'select':
        input = (
          <Select
            {...{
              name,
              label,
              placeholder,
              value,
              required,
              disabled,
              readOnly,
              options,
              error: !!error && !focus,
              onFocus: this.onFocusHandler,
              onBlur: this.onBlurHandler,
              onChange: this.onChangeHandler,
            }}
          />
        );
        break;

      case 'textarea':
        input = (
          <TextArea
            {...{
              name,
              label,
              placeholder,
              value,
              required,
              disabled,
              readOnly,
              error: !!error && !focus,
              onFocus: this.onFocusHandler,
              onBlur: this.onBlurHandler,
              onChange: this.onChangeHandler,
            }}
          />
        );
        break;

      case 'text':
      case 'hidden':
      case 'tel':
      case 'email':
      case 'password':
        input = (
          <TextInput
            {...{
              type,
              name,
              label,
              placeholder,
              value,
              required,
              disabled,
              readOnly,
              autoComplete,
              error: !!error && !focus,
              onFocus: this.onFocusHandler,
              onBlur: this.onBlurHandler,
              onChange: this.onChangeHandler,
            }}
          />
        );
        break;

      case 'file':
      case 'image':
        input = (
          <Image
            {...{
              type,
              name,
              label,
              placeholder,
              value,
              required,
              disabled,
              readOnly,
              index,
              preview,
              error: !!error && !focus,
              onChange: this.onChangeHandler,
            }}
          />
        );
        break;

      case 'cleave':
        input = (
          <CleaveInput
            {...{
              type,
              name,
              label,
              placeholder,
              value,
              required,
              disabled,
              readOnly,
              options,
              error: !!error && !focus,
              onFocus: this.onFocusHandler,
              onBlur: this.onBlurHandler,
              onChange: this.onChangeHandler,
            }}
          />
        );
        break;

      case 'tags':
        input = (
          <TagsInput
            {...{
              type,
              name,
              label,
              placeholder,
              value,
              required,
              disabled,
              readOnly,
              options,
              error: !!error && !focus,
              onFocus: this.onFocusHandler,
              onBlur: this.onBlurHandler,
              onChange: this.onChangeHandler,
            }}
          />
        );
        break;

      case 'datepicker':
        input = (
          <DatePickerInput
            {...{
              type,
              name,
              label,
              placeholder,
              value,
              multiple,
              required,
              disabled,
              readOnly,
              format,
              error: !!error && !focus,
              onFocus: this.onFocusHandler,
              onBlur: this.onBlurHandler,
              onChange: this.onChangeHandler,
            }}
          />
        );
        break;

      case 'googleplaces':
        input = (
          <GooglePlacesInput
            {...{
              type,
              name,
              label,
              placeholder,
              value,
              required,
              disabled,
              readOnly,
              error: !!error && !focus,
              onFocus: this.onFocusHandler,
              onBlur: this.onBlurHandler,
              onChange: this.onChangeHandler,
            }}
          />
        );
        break;

      default:
      // no-op
    }

    return input;
  }

  /**
   * Render Input.
   *
   * @return {JSX}
   */
  render() {
    const {
      children,
      name,
      error,
      focus,
      type,
      label,
      description,
      resendPhoneLink,
      colWidth,
      mobileColWidth,
      clearRight,
      light,
      tool_tip,
    } = this.props;

    return (
      <>
        <div
          className={classNames(styles.wrapper, styles[type], {
            [styles.error]: !!error && !focus,
            [styles[`col${colWidth}`]]: colWidth,
            [styles[`mobile-col${mobileColWidth}`]]: mobileColWidth,
            [styles.light]: light,
          })}
        >
          <label htmlFor={name} className={styles.inner}>
            <div
              className={classNames(styles.label, {
                [styles.focus]: focus,
              })}
            >
              <div className={styles.labelWrapper}>
                {label && <p className={styles.labelText}>{label}</p>}
                {tool_tip && (
                  <ToolTip title={tool_tip}>
                    <BlackTieIcon
                      icon="info-circle"
                      weight="btr"
                      className={styles.toolTip}
                    />
                  </ToolTip>
                )}
              </div>
              {error && (
                <Message className={styles.errorMessage} message={error} />
              )}
            </div>

            {this.renderInput()}

            {description && <p className={styles.description}>{description}</p>}

            {resendPhoneLink && (
              <p className={styles.description}>
                {p.t('profile.form.phone_number.resend_text')}{' '}
                <NavLink to={createUrl('updatePhone')}>
                  {p.t('profile.form.phone_number.resend_link')}
                </NavLink>
              </p>
            )}
          </label>

          {children}
        </div>
        {clearRight && <div className={styles.clear} />}
      </>
    );
  }
}
